import React, { useEffect, useState } from "react";
import { CheckIcon, EyeIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOrders,
  updateOrders,
  getOrdersByCompanyId,
} from "../../features/admin/OrderSlice";
import { getCompanyByUserId } from "../../features/admin/CompanySlice";
import OrderModal from "../../components/admin/OrderModal";
import ConfirmModal from "../../components/ConfirmModal";
import CustomTableComponent from "../../components/CustomTableComponent"; // <- Dùng đúng component bảng

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const userId = localStorage.getItem("userId");
const hasPermission = (slug) => {
  if (userId == 1) return true;
  return getUserPermissions().includes(slug);
};

const OrderListFeature = () => {
  const dispatch = useDispatch();
  const { orders, status, error } = useSelector((state) => state.orders);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const [acceptId, setAcceptId] = useState(null);

  useEffect(() => {
    getCompanyInfo();
    // eslint-disable-next-line
  }, []);

  const getCompanyInfo = async () => {
    const companyData = await dispatch(getCompanyByUserId(userId)).unwrap();
    setCompanyInfo(companyData);
    await getOrderData(companyData?.id);
  };

  const getOrderData = async (id) => {
    await dispatch(getOrdersByCompanyId(id)).unwrap();
  };

  const handleOpenModal = (item = null) => {
    setCurrentOrder(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentOrder(null);
  };

  const handleOpenConfirm = (id) => {
    setIsOpenConfirm(true);
    setAcceptId(id);
  };

  const handleCloseConfirm = () => {
    setIsOpenConfirm(false);
    setAcceptId(null);
  };

  const handleAcceptConfirm = () => {
    handleUpdateStatus(acceptId);
    handleCloseConfirm();
  };

  const handleUpdateStatus = async (id) => {
    try {
      await dispatch(
        updateOrders({ id: id, newPost: { status: "success" } }),
      ).unwrap();
      await getOrderData(companyInfo?.id);
      toast.success("Duyệt giao dịch thành công.");
    } catch (error) {
      toast.error("Đã xảy ra lỗi! Vui lòng thử lại.");
      console.error("Lỗi khi duyệt đơn hàng:", error);
    }
  };

  // TABLE HEADERS
  const headers = [
    {
      key: "id",
      label: "Mã giao dịch",
      render: (item) => <span className="font-semibold">{item.id}</span>,
    },
    {
      key: "fullName",
      label: "Người đặt hàng",
      render: (item) => <span className="font-medium">{item.fullName}</span>,
    },
    { key: "phone", label: "Số điện thoại" },
    { key: "province", label: "Tỉnh/Thành phố" },
    { key: "district", label: "Quận/Huyện" },
    {
      key: "status",
      label: "Trạng thái",
      render: (item) => (
        <span
          className={`px-3 py-1 rounded-full text-sm ${
            item.status === "success"
              ? "bg-green-100 text-green-800"
              : item.status === "pending"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-red-100 text-red-800"
          } whitespace-nowrap`}
        >
          {item.status === "pending"
            ? "Chờ xác nhận"
            : item.status === "success"
              ? "Đã xác nhận"
              : "Đã huỷ"}
        </span>
      ),
    },
  ];

  if (hasPermission("BILL_UPDATE")) {
    headers.push({
      key: "setting",
      label: "Thao tác",
      render: (item) => (
        <div className="flex space-x-2">
          <button
            disabled={item.status === "success"}
            onClick={() => handleOpenConfirm(item.id)}
            title="Duyệt"
            className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded"
          >
            <CheckIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleOpenModal(item)}
            title="Xem chi tiết"
            className="btn btn-outline btn-info btn-sm px-3 my-1 rounded"
          >
            <EyeIcon className="w-5 h-5" />
          </button>
        </div>
      ),
    });
  } else {
    headers.push({
      key: "setting",
      label: "Thao tác",
      render: (item) => (
        <button
          onClick={() => handleOpenModal(item)}
          title="Xem chi tiết"
          className="btn btn-outline btn-info btn-sm px-3 my-1 rounded"
        >
          <EyeIcon className="w-5 h-5" />
        </button>
      ),
    });
  }

  // SEARCH
  const filterData = orders.filter((item) =>
    searchValue === ""
      ? true
      : [item.fullName, item.phone, item.id]
          .map((field) => (field ? String(field).toLowerCase() : ""))
          .some((field) => field.includes(searchValue.toLowerCase())),
  );

  return (
    <div className="p-6 bg-white rounded-2xl shadow-md border mb-8">
      <div>
        {/* Tiêu đề */}
        <h2 className="text-2xl font-bold text-emerald-700 mb-1">
          Quản lý đơn hàng của bạn
        </h2>
        <p className="text-gray-500 text-sm mb-3">
          Danh sách đơn hàng của đơn vị bạn
        </p>
      </div>

      {/* Tìm kiếm */}
      <div className="flex flex-wrap gap-2 mb-3">
        <label className="text-sm font-medium w-24">Tìm kiếm:</label>
        <input
          type="search"
          className="w-full max-w-xs px-2 py-1 border rounded-lg focus:outline-none pr-10"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="rounded-lg shadow">
        {status === "loading" ? (
          <div className="flex justify-center items-center h-52">
            <ArrowPathIcon className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : status === "failed" ? (
          <div className="p-4 text-red-500 bg-red-50 rounded-md">
            Đã xảy ra lỗi: {error}
          </div>
        ) : (
          <CustomTableComponent headers={headers} data={filterData} />
        )}
      </div>

      {/* Modal chi tiết */}
      {isModalOpen && (
        <OrderModal
          orderId={currentOrder?.id || null}
          onClose={handleCloseModal}
        />
      )}

      {/* Modal xác nhận duyệt */}
      <ConfirmModal
        isOpen={isOpenConfirm}
        onClose={handleCloseConfirm}
        onSubmit={handleAcceptConfirm}
        message={"Bạn có muốn duyệt đơn hàng này không?"}
      />
    </div>
  );
};

export default OrderListFeature;
